/* =====================================================================
   Don Barato · global.js
   Lo compartido por todas las páginas:
   - DonBarato.formatMoney(cents) → "$12.990"
   - DonBarato.fetchSections(['cart-drawer'], url) → { 'cart-drawer': '<html>' }
   - Cajones: [data-drawer-open="ID"] / [data-drawer-close] · DonBarato.openDrawer(id) / closeDrawer(id)
   - Contador del carro: escucha el evento 'cart:updated' (detail.cart = JSON del carro)
   - Carro: si existe window.DonBaratoCart (assets/cart.js), el ícono del carro y los «+» de las
     tarjetas trabajan sin recargar; si no, caen al comportamiento normal (ir a /cart, enviar el form).
   ===================================================================== */
(function () {
  const DB = (window.DonBarato = window.DonBarato || {});
  const peso = new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 });

  DB.formatMoney = function (cents) {
    return '$' + peso.format(Math.round((Number(cents) || 0) / 100));
  };

  DB.fetchSections = async function (sections, url) {
    const base = url || window.location.pathname;
    const sep = base.includes('?') ? '&' : '?';
    // Sin «Accept: application/json»: con esa cabecera, /products/… y /cart responden su propio JSON y no las secciones.
    const res = await fetch(base + sep + 'sections=' + sections.join(','));
    if (!res.ok) throw new Error('No se pudieron cargar las secciones');
    return res.json();
  };

  DB.parseHTML = function (html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return selector ? doc.querySelector(selector) : doc;
  };

  /* ---------- Cajones ---------- */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  // Un cajón puede abrirse sobre otro (la hoja rápida sobre el carro): cada uno recuerda quién lo abrió,
  // y Escape y Tab actúan sobre el de más arriba (el último abierto en el DOM).
  const triggers = {};
  const topDrawer = () => {
    const open = document.querySelectorAll('.drawer.is-open');
    return open[open.length - 1] || null;
  };

  DB.openDrawer = function (id, trigger) {
    const drawer = document.getElementById(id);
    if (!drawer) return;
    triggers[id] = trigger || document.activeElement;
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      const target = drawer.querySelector('[data-drawer-focus]') || drawer.querySelector(FOCUSABLE);
      if (target) target.focus({ preventScroll: true });
    });
    document.body.classList.add('is-locked');
  };

  DB.closeDrawer = function (id) {
    const drawer = typeof id === 'string' ? document.getElementById(id) : id;
    if (!drawer || drawer.hidden) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    const done = () => {
      drawer.hidden = true;
      if (!document.querySelector('.drawer.is-open')) document.body.classList.remove('is-locked');
    };
    const panel = drawer.querySelector('.drawer__panel');
    if (panel && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      panel.addEventListener('transitionend', done, { once: true });
      setTimeout(done, 400);
    } else {
      done();
    }
    const trigger = triggers[drawer.id];
    if (trigger && document.contains(trigger)) trigger.focus({ preventScroll: true });
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      DB.openDrawer(opener.getAttribute('data-drawer-open'), opener);
      return;
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      e.preventDefault();
      DB.closeDrawer(closer.closest('.drawer'));
    }
  });

  document.addEventListener('keydown', (e) => {
    const open = topDrawer();
    if (!open) return;
    if (e.key === 'Escape') {
      DB.closeDrawer(open);
      return;
    }
    if (e.key === 'Tab') {
      const items = [...open.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  /* ---------- Contador del carro ---------- */
  document.addEventListener('cart:updated', (e) => {
    const cart = e.detail && e.detail.cart;
    if (!cart) return;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
  });

  /* ---------- Ícono del carro → abre el carro lateral ---------- */
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-cart-toggle]');
    if (!toggle || !window.DonBaratoCart) return;
    e.preventDefault();
    window.DonBaratoCart.open(toggle);
  });

  /* ---------- Agregar sin enviar el formulario ----------
     El analítico de Shopify (trekkie) cuenta «agregado al carro» en el envío del formulario Y en la llamada a /cart/add.js:
     cada clic llegaba 2 veces a Meta y Google. Con el carro AJAX, el clic (o Enter) en el botón no envía el formulario:
     avisa con «db:submit» y cada flujo agrega por AJAX (1 solo evento). Sin JS, el formulario se envía normal. */
  const FORMS_AJAX = 'form[data-product-form], form[data-quick-add], form[data-qa-form]';
  window.addEventListener(
    'click',
    (e) => {
      const btn = e.target.closest('button[type="submit"], input[type="submit"]');
      if (!btn || btn.matches('[data-buy-now]') || !window.DonBaratoCart) return;
      const form = btn.form;
      if (!form || !form.matches(FORMS_AJAX)) return;
      e.preventDefault();
      const ev = new CustomEvent('db:submit', { bubbles: true, cancelable: true });
      ev.submitter = btn;
      form.dispatchEvent(ev);
    },
    true
  );

  /* ---------- «+» de las tarjetas: agregar sin recargar ---------- */
  const quickAdd = async (e) => {
    const form = e.target.closest('form[data-quick-add]');
    if (!form || !window.DonBaratoCart) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const data = new FormData(form);
    btn && btn.classList.add('is-loading');
    try {
      await window.DonBaratoCart.add([{ id: Number(data.get('id')), quantity: Number(data.get('quantity') || 1) }], { trigger: btn });
      if (btn) {
        btn.classList.add('is-added');
        setTimeout(() => btn.classList.remove('is-added'), 1600);
      }
    } catch (err) {
      DB.toast((err && err.message) || 'No se pudo agregar. Intenta de nuevo.');
    } finally {
      btn && btn.classList.remove('is-loading');
    }
  };
  document.addEventListener('db:submit', quickAdd);
  document.addEventListener('submit', quickAdd);

  /* ---------- Aviso flotante ---------- */
  DB.toast = function (msg) {
    let el = document.querySelector('.toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'toast';
      el.setAttribute('role', 'status');
      document.body.appendChild(el);
    }
    el.textContent = msg;
    el.classList.add('is-on');
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('is-on'), 3200);
  };
})();
